import { useState } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    CircularProgress
} from '@mui/material';
import { FileUploadOutlined, CloseOutlined, CloudUploadOutlined, FileDownloadOutlined, InfoOutlined } from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { uploadCreditBalanceApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';
// import { formatINR } from './creditLimitsUtils';

const BulkUploadModal = ({ open, onClose, onSuccess }) => {
    const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'json'
    const [uploadedFile, setUploadedFile] = useState(null);
    const [jsonPayloadText, setJsonPayloadText] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    const handleDownloadTemplate = () => {
        const templateData = [
            {
                email: 'employee1@corporate.com',
                cross_user_id: 145,
                amount: 5000,
                category: 'TA',
                remarks: 'September TA'
            },
            {
                email: 'employee2@corporate.com',
                cross_user_id: '',
                amount: 3000,
                category: 'Bonus',
                remarks: 'Performance Bonus'
            },
            {
                email: '',
                cross_user_id: 147,
                amount: 2500,
                category: 'Allowance',
                remarks: 'Travel Allowance'
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Credit_Adjustments');
        XLSX.writeFile(workbook, 'Credit_Adjustments_Template.xlsx');
    };

    const handleLoadSampleJson = () => {
        const sample = [
            {
                email: 'employee1@corporate.com',
                cross_user_id: 145,
                amount: 5000,
                category: 'TA',
                remarks: 'September TA'
            },
            {
                email: 'employee2@corporate.com',
                amount: 3000,
                category: 'Bonus',
                remarks: 'Performance Bonus'
            },
            {
                cross_user_id: 147,
                amount: 2500,
                category: 'Allowance',
                remarks: 'Travel Allowance'
            }
        ];
        setJsonPayloadText(JSON.stringify(sample, null, 2));
    };

    const handleClose = () => {
        if (isUploading) return;
        setUploadedFile(null);
        setJsonPayloadText('');
        setUploadResult(null);
        onClose();
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();

        let payloadToSend;

        if (uploadMode === 'file') {
            if (!uploadedFile) {
                globalNotifyError('Please select an Excel (.xlsx, .xls) or CSV file to upload');
                return;
            }

            if (uploadedFile.size > 10 * 1024 * 1024) {
                globalNotifyError('File size exceeds 10MB limit');
                return;
            }

            const formData = new FormData();
            formData.append('file', uploadedFile);
            payloadToSend = formData;
        } else {
            const raw = jsonPayloadText.trim();
            if (!raw) {
                globalNotifyError('Please enter or paste a JSON array');
                return;
            }

            let parsed;
            try {
                parsed = JSON.parse(raw);
            } catch (err) {
                globalNotifyError('Invalid JSON format: ' + err.message);
                return;
            }

            if (!Array.isArray(parsed)) {
                globalNotifyError('JSON payload must be an array of objects [ { ... } ]');
                return;
            }

            if (parsed.length === 0) {
                globalNotifyError('JSON array cannot be empty');
                return;
            }

            for (let i = 0; i < parsed.length; i++) {
                const item = parsed[i];
                if (!item || typeof item !== 'object') {
                    globalNotifyError(`Item #${i + 1} must be an object`);
                    return;
                }
                const hasEmail = Boolean(item.email && String(item.email).trim());
                const hasCrossUserId = Boolean(
                    item.cross_user_id !== undefined && item.cross_user_id !== null && String(item.cross_user_id).trim() !== ''
                );
                if (!hasEmail && !hasCrossUserId) {
                    globalNotifyError(`Item #${i + 1} requires either 'email' or 'cross_user_id'`);
                    return;
                }
                const amt = Number(item.amount);
                if (isNaN(amt) || amt <= 0) {
                    globalNotifyError(`Item #${i + 1} must have a valid 'amount' greater than 0`);
                    return;
                }
            }

            payloadToSend = parsed;
        }

        setIsUploading(true);
        setUploadResult(null);
        try {
            const response = await uploadCreditBalanceApi(payloadToSend);
            if (response?.data?.success) {
                const resData = response.data;
                const count = resData?.data?.processed_count ?? (Array.isArray(payloadToSend) ? payloadToSend.length : '');
                const total = resData?.data?.total_amount;
                // const successMsg =
                //     resData.message ||
                //     `Successfully processed credit adjustments${count ? ` for ${count} employees` : ''}.${
                //         total ? ` Total credited: ${formatINR(total)}.` : ''
                //     }`;

                if (resData.data?.errors && resData.data.errors.length > 0) {
                    setUploadResult(resData.data);
                } else {
                    handleClose();
                }
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else if (response?.response?.data?.message) {
            } else {
                handleClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error uploading credit balance adjustments:', error);
            if (error?.response?.data?.message) {
            } else {
                handleCatchError(error);
            }
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#f0fdf4', color: '#16a34a', display: 'flex' }}>
                        <FileUploadOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Bulk Upload Credit Adjustments
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Upload bulk balance credits via Excel/CSV file or JSON Array
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={handleClose} disabled={isUploading}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleUploadSubmit}>
                <DialogContent sx={{ pt: 1.5 }}>
                    <Stack spacing={2.5}>
                        {/* Option Selector Toggle */}
                        <Box sx={{ display: 'flex', p: 0.5, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                            <Button
                                fullWidth
                                size="small"
                                onClick={() => setUploadMode('file')}
                                sx={{
                                    borderRadius: 1.5,
                                    py: 0.8,
                                    textTransform: 'none',
                                    fontWeight: uploadMode === 'file' ? 700 : 500,
                                    color: uploadMode === 'file' ? '#16a34a' : 'text.secondary',
                                    bgcolor: uploadMode === 'file' ? '#fff' : 'transparent',
                                    boxShadow: uploadMode === 'file' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    '&:hover': { bgcolor: uploadMode === 'file' ? '#fff' : 'rgba(0,0,0,0.04)' }
                                }}
                            >
                                Option A: Excel / CSV File
                            </Button>
                            <Button
                                fullWidth
                                size="small"
                                onClick={() => setUploadMode('json')}
                                sx={{
                                    borderRadius: 1.5,
                                    py: 0.8,
                                    textTransform: 'none',
                                    fontWeight: uploadMode === 'json' ? 700 : 500,
                                    color: uploadMode === 'json' ? '#16a34a' : 'text.secondary',
                                    bgcolor: uploadMode === 'json' ? '#fff' : 'transparent',
                                    boxShadow: uploadMode === 'json' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    '&:hover': { bgcolor: uploadMode === 'json' ? '#fff' : 'rgba(0,0,0,0.04)' }
                                }}
                            >
                                Option B: JSON Payload
                            </Button>
                        </Box>

                        {/* Option A: Excel / CSV File Upload */}
                        {uploadMode === 'file' && (
                            <>
                                {/* Template Download Prompt Banner */}
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: '#eff6ff',
                                        border: '1px solid #bfdbfe',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                                        <InfoOutlined color="primary" fontSize="small" sx={{ mt: 0.3, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={700} color="primary.dark">
                                                Need sample template format?
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}
                                            >
                                                Columns: <b>email</b> (optional if cross_user_id provided), <b>cross_user_id</b> (optional
                                                if email provided), <b>amount</b> (required), <b>category</b> (optional), <b>remarks</b>{' '}
                                                (optional)
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<FileDownloadOutlined fontSize="small" />}
                                        onClick={handleDownloadTemplate}
                                        sx={{
                                            borderRadius: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            bgcolor: '#fff',
                                            borderColor: '#93c5fd',
                                            color: '#1d4ed8',
                                            '&:hover': { bgcolor: '#dbeafe', borderColor: '#60a5fa' }
                                        }}
                                    >
                                        Template
                                    </Button>
                                </Box>

                                {/* Dropzone */}
                                <Box
                                    component="label"
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: uploadedFile ? '#16a34a' : 'divider',
                                        borderRadius: 3,
                                        p: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: uploadedFile ? '#f0fdf4' : '#fff',
                                        display: 'block',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' }
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept=".xlsx, .xls, .csv"
                                        hidden
                                        onChange={(e) => {
                                            if (e.target.files?.[0]) {
                                                setUploadedFile(e.target.files[0]);
                                            }
                                        }}
                                    />
                                    <CloudUploadOutlined sx={{ fontSize: 42, color: uploadedFile ? '#16a34a' : 'text.disabled', mb: 1 }} />
                                    {uploadedFile ? (
                                        <>
                                            <Typography variant="subtitle1" fontWeight={700} color="#15803d">
                                                {uploadedFile.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(uploadedFile.size / 1024).toFixed(1)} KB — Click to choose different file
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Choose an Excel (.xlsx, .xls) or CSV file
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Header: multipart/form-data | Form Key: file (Max 10MB)
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}

                        {/* Option B: JSON Payload */}
                        {uploadMode === 'json' && (
                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                        Request Body: JSON Array (application/json)
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={handleLoadSampleJson}
                                        sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0 }}
                                    >
                                        Insert Sample JSON
                                    </Button>
                                </Box>
                                <TextField
                                    multiline
                                    rows={8}
                                    fullWidth
                                    value={jsonPayloadText}
                                    onChange={(e) => setJsonPayloadText(e.target.value)}
                                    placeholder={`[\n  {\n    "email": "employee1@corporate.com",\n    "cross_user_id": 145,\n    "amount": 5000,\n    "category": "TA",\n    "remarks": "Sept TA"\n  },\n  {\n    "email": "employee2@corporate.com",\n    "amount": 3000,\n    "category": "Bonus",\n    "remarks": "Performance Bonus"\n  },\n  {\n    "cross_user_id": 147,\n    "amount": 2500,\n    "category": "Allowance",\n    "remarks": "Travel Allowance"\n  }\n]`}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '0.82rem',
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2
                                        }
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Fields per object: <b>email</b> (optional if cross_user_id provided), <b>cross_user_id</b> (optional if
                                    email provided), <b>amount</b> (required &gt; 0), <b>category</b> (optional), <b>remarks</b> (optional)
                                </Typography>
                            </Stack>
                        )}

                        {/* Partial Errors Alert */}
                        {uploadResult?.errors && uploadResult.errors.length > 0 && (
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                                <Typography variant="caption" fontWeight={700} color="#b91c1c" display="block" mb={0.5}>
                                    Partial Errors ({uploadResult.errors.length}):
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', color: '#dc2626' }}>
                                    {uploadResult.errors.map((err, idx) => (
                                        <li key={idx}>{typeof err === 'object' ? JSON.stringify(err) : String(err)}</li>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={isUploading} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isUploading || (uploadMode === 'file' ? !uploadedFile : !jsonPayloadText.trim())}
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlined />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#16a34a',
                            '&:hover': { bgcolor: '#15803d' }
                        }}
                    >
                        {isUploading ? 'Processing...' : 'Upload Adjustments'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

BulkUploadModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default BulkUploadModal;
